import React from 'react';
import {action, observable, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {
    IHistoryConsoleService,
    IHistoryConsoleSearchParams
} from '@techsee/techsee-client-infra/lib/services/HistoryConsoleService';
import {IRedirectionService, IUrlParams} from '../../services/RedirectionService';
import {EVENT_LEVELS, EVENT_SOURCES, LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {email} from '@techsee/techsee-common/lib/constants/utils.patterns';
import {AppState} from '../../app.state';
import tsDate from '@techsee/techsee-date';

export const QUICK_DATE_RANGES = ['today', 'yesterday', 'last7days'] as const;
const PHONE_NUMBER_REGEX = /^[0-9+\-() ]+$/;
export type QuickDateRange = (typeof QUICK_DATE_RANGES)[number] | null;

export class HistoryConsoleFilterState {
    @observable formError: boolean = false;
    @observable dateError: boolean = false;
    @observable customerRef: string = '';
    @observable customerPhone: string = '';
    @observable customerEmail: string = '';
    @observable roomId: string = '';
    @observable preDataCollectionCode: string = '';
    @observable customerIdentifier: string = ''; // combined input value displayed in the UI
    @observable searchText: string = '';
    @observable startDate: number = 0;
    @observable endDate: number = 0;
    @observable isLoading: boolean = false;
    @observable searchError: string = '';
    @observable activeQuickFilter: QuickDateRange = null;
}

const CUSTOMER_IDENTIFIER_FIELDS = ['customerRef', 'customerPhone', 'customerEmail'] as const;
const DIRECT_LOOKUP_FIELDS = ['roomId', 'preDataCollectionCode'] as const;
const HISTORY_CONSOLE_EXCLUSIVE_FIELDS = [...CUSTOMER_IDENTIFIER_FIELDS, ...DIRECT_LOOKUP_FIELDS] as const;

export type HistoryConsoleExclusiveField = (typeof HISTORY_CONSOLE_EXCLUSIVE_FIELDS)[number];

export interface IHistoryConsoleFilterController {
    filterState: HistoryConsoleFilterState;
    translate: (token: string, data?: Record<string, any>) => string;
    dateFormat: string;
    maxDate: Date;
    isFormValid: boolean;
    canUseExclusiveField(fieldName: HistoryConsoleExclusiveField): boolean;

    setCustomerIdentifier(value: string): void;
    setRoomId(value: string): void;
    setPreDataCollectionCode(value: string): void;
    setStartDate(value: number): void;
    setEndDate(value: number): void;
    setQuickDateRange(range: QuickDateRange): void;
    onSubmit(event: React.FormEvent): void;
    clearForm(): void;
}

export class HistoryConsoleFilterController extends TechseeBaseController implements IHistoryConsoleFilterController {
    filterState: HistoryConsoleFilterState;
    private readonly _accountId: string;
    private readonly _userId: string;
    private readonly _redirectionService: IRedirectionService;
    private readonly _dateFormat: string;
    private readonly _historyConsoleService: IHistoryConsoleService;
    private readonly _appState: AppState;

    constructor(
        accountId: string,
        redirectionService: IRedirectionService,
        dateFormat: string,
        historyConsoleService: IHistoryConsoleService,
        appState: AppState,
        userId: string
    ) {
        super();
        this.filterState = new HistoryConsoleFilterState();
        this._accountId = accountId;
        this._userId = userId;
        this._redirectionService = redirectionService;
        this._dateFormat = dateFormat.split(' ')[0];
        this._historyConsoleService = historyConsoleService;
        this._appState = appState;
    }

    get dateFormat(): string {
        return this._dateFormat;
    }

    get maxDate(): Date {
        return tsDate().endOf('day').toDate();
    }

    get hasCustomerIdentifier(): boolean {
        return !!(this.filterState.customerRef || this.filterState.customerPhone || this.filterState.customerEmail);
    }

    get isFormValid(): boolean {
        return this.hasCustomerIdentifier || !!this.filterState.roomId || !!this.filterState.preDataCollectionCode;
    }

    canUseExclusiveField(fieldName: HistoryConsoleExclusiveField): boolean {
        if ((CUSTOMER_IDENTIFIER_FIELDS as ReadonlyArray<string>).includes(fieldName)) {
            return DIRECT_LOOKUP_FIELDS.every((f) => !this.filterState[f]);
        }

        return (
            !this.hasCustomerIdentifier && DIRECT_LOOKUP_FIELDS.every((f) => f === fieldName || !this.filterState[f])
        );
    }

    @action
    setCustomerIdentifier(value: string) {
        this.filterState.customerIdentifier = value;

        // Detect type and route to the correct field; clear the others
        if (email.test(value)) {
            this.filterState.customerEmail = value;
            this.filterState.customerPhone = '';
            this.filterState.customerRef = '';
        } else if (PHONE_NUMBER_REGEX.test(value) && value.trim().length > 0) {
            this.filterState.customerPhone = value;
            this.filterState.customerEmail = '';
            this.filterState.customerRef = '';
        } else {
            this.filterState.customerRef = value;
            this.filterState.customerEmail = '';
            this.filterState.customerPhone = '';
        }
    }

    @action setRoomId(value: string) {
        this.filterState.roomId = value;
    }
    @action setPreDataCollectionCode(value: string) {
        this.filterState.preDataCollectionCode = value;
    }

    @action
    setStartDate(value: number) {
        this.filterState.startDate = value;
    }

    @action
    setEndDate(value: number) {
        this.filterState.endDate = value;
    }

    @action
    setQuickDateRange(range: QuickDateRange) {
        this.filterState.activeQuickFilter = range;

        if (range === 'today') {
            this.filterState.startDate = tsDate().startOf('day').valueOf();
            this.filterState.endDate = tsDate().endOf('day').valueOf();
        } else if (range === 'yesterday') {
            this.filterState.startDate = tsDate().subtract(1, 'day').startOf('day').valueOf();
            this.filterState.endDate = tsDate().subtract(1, 'day').endOf('day').valueOf();
        } else if (range === 'last7days') {
            this.filterState.startDate = tsDate().subtract(6, 'days').startOf('day').valueOf();
            this.filterState.endDate = tsDate().endOf('day').valueOf();
        } else {
            this.filterState.startDate = 0;
            this.filterState.endDate = 0;
        }
    }

    @action
    clearForm() {
        this.filterState.customerRef = '';
        this.filterState.customerPhone = '';
        this.filterState.customerEmail = '';
        this.filterState.customerIdentifier = '';
        this.filterState.roomId = '';
        this.filterState.preDataCollectionCode = '';
        this.filterState.startDate = 0;
        this.filterState.endDate = 0;
        this.filterState.formError = false;
        this.filterState.dateError = false;
        this.filterState.activeQuickFilter = null;
    }

    onSubmit(event: React.FormEvent) {
        event && event.preventDefault();

        if (this.filterState.isLoading) {
            return;
        }

        if (!this.isFormValid) {
            runInAction(() => {
                this.filterState.formError = true;
            });

            return;
        }

        runInAction(() => {
            this.filterState.formError = false;
            this.filterState.dateError = false;
            this.filterState.searchError = '';
        });

        if (
            this.filterState.startDate &&
            this.filterState.endDate &&
            new Date(this.filterState.startDate) > new Date(this.filterState.endDate)
        ) {
            runInAction(() => {
                this.filterState.dateError = true;
            });

            return;
        }

        this.addEventLog({
            type: LOG_EVENTS.moveToHistoryConsole,
            accountId: this._accountId,
            userId: this._userId,
            level: EVENT_LEVELS.info,
            sentBy: EVENT_SOURCES.dashboard,
            meta: {source: 'Invite'}
        });

        this.fetchSessions();
    }

    private async fetchSessions() {
        runInAction(() => {
            this.filterState.isLoading = true;
        });

        try {
            const params: IHistoryConsoleSearchParams = {};

            if (this.filterState.customerRef) {
                params.customerId = this.filterState.customerRef;
            }
            if (this.filterState.customerPhone) {
                params.customerPhone = this.filterState.customerPhone;
            }
            if (this.filterState.customerEmail) {
                params.customerEmail = this.filterState.customerEmail;
            }
            if (this.filterState.roomId) {
                params.sessionId = this.filterState.roomId;
            }
            if (this.filterState.preDataCollectionCode) {
                params.preDataCollectionCode = this.filterState.preDataCollectionCode;
            }
            if (this.filterState.startDate) {
                params.startDate = this.filterState.startDate;
            }
            if (this.filterState.endDate) {
                params.endDate = this.filterState.endDate;
            }

            const data = await this._historyConsoleService.getSessions(params);
            const results = data.historyConsoleDataSessions || [];

            if (results.length === 0) {
                runInAction(() => {
                    this.filterState.searchError = 'REACT.HISTORY_CONSOLE.FILTER.NO_RESULTS';
                    this.filterState.isLoading = false;
                });

                return;
            }

            runInAction(() => {
                this._appState.historyConsoleSessionsResults = results;
                this._appState.historyConsoleTotalResults = data.totalResults || 0;
                this.filterState.isLoading = false;
            });

            const urlParams: IUrlParams = {
                customerRef: this.filterState.customerRef || undefined,
                customerPhone: this.filterState.customerPhone || undefined,
                customerEmail: this.filterState.customerEmail || undefined,
                startDate: this.filterState.startDate || undefined,
                endDate: this.filterState.endDate || undefined,
                roomId: this.filterState.roomId || undefined,
                preDataCollectionCode: this.filterState.preDataCollectionCode || undefined
            };

            this._redirectionService.goToHistoryConsole(urlParams);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Search failed';

            this.addEventLog({
                type: LOG_EVENTS.moveToHistoryConsoleFailed,
                accountId: this._accountId,
                userId: this._userId,
                level: EVENT_LEVELS.error,
                sentBy: EVENT_SOURCES.dashboard,
                meta: {error: message}
            });

            runInAction(() => {
                this.filterState.searchError = message;
                this.filterState.isLoading = false;
            });
        }
    }
}
